/**
 * React Query primitives for the cart quote (POST /permits/cart).
 *
 * Quote identity == the exact normalized request body. ONE normalizer
 * (`normalizeCartQuoteRequest`) produces BOTH the POST body and the react-query
 * cache key, so key ≡ request by construction. The item array order is
 * preserved exactly — tiered pricing ordinals are order-sensitive, so items are
 * never sorted, grouped, or deduped. Every field a CART_TYPES producer puts on
 * a cart item is carried forward (pricing inputs like `id`/`type`/`quantity`/
 * `change_request` AND display metadata the response/drawer consumes like
 * `changeList`); only fields on the inert-exclusion allowlist are stripped.
 *
 * `date_added` is the sole excluded field: addToCart.js / replaceItemInCart.js
 * stamp it with `Date.now()` on every item, but it is a client-only cart-expiry
 * timestamp — the CartDrawer reads it off the redux cart to age items out
 * BEFORE the quote call, never back off the POST /permits/cart response — so
 * stripping it from the request changes no consumer (proven inert by test).
 *
 * @module useCartQuote
 */

import { useQuery } from "@tanstack/react-query";
import { getCartInformation } from "../services/permits";

/**
 * Client-only cart-item fields excluded from the quote request/key because they
 * are proven inert — they do not affect the POST /permits/cart response or any
 * consumer of it. Exclusion-only allowlist: every other field passes through.
 * @type {string[]}
 */
export const INERT_CART_ITEM_FIELDS = ["date_added"];

/**
 * Strips inert client-only fields from a single cart item while preserving the
 * order and value of every response-affecting field.
 * @param {Object} item - a cart item as produced by a CART_TYPES producer
 * @returns {Object} the item without inert fields
 */
const stripInertFields = (item) => {
	const result = { ...item };
	for (const field of INERT_CART_ITEM_FIELDS) delete result[field];
	return result;
};

/**
 * Produces the exact POST /permits/cart request body for a cart snapshot. This
 * is the single source of quote identity — `cartQuoteKey` and the hook's
 * `queryFn` both derive from it, so the cache key and the request body can
 * never diverge. Item order is preserved verbatim; only inert fields are
 * dropped; `payment_type` is included only when provided (mirrors
 * `getCartInformation`'s body shape exactly).
 * @param {Array<Object>} [items] - ordered cart items (redux cart snapshot)
 * @param {string} [paymentType] - selected payment method, if any
 * @returns {{ items: Array<Object>, payment_type?: string }} the normalized body
 */
export const normalizeCartQuoteRequest = (items = [], paymentType) => ({
	items: items.map(stripInertFields),
	...(paymentType && { payment_type: paymentType }),
});

/**
 * Serializes a value with OBJECT keys in sorted order while preserving ARRAY
 * order exactly. Object key order never reaches pricing (the server reads
 * fields by name), but producers construct the same logical item with
 * different key orders - the apply flow appends `{type, id}` while addToCart
 * stores `{id, type}` - and the cache key must treat those as identical or a
 * primed entry is silently missed. Array order stays significant: tiered
 * pricing ordinals depend on item order.
 * @param {*} value - the value to serialize
 * @returns {string|undefined} canonical JSON, or undefined like JSON.stringify
 */
const stableSerialize = (value) => {
	if (Array.isArray(value)) return `[${value.map((entry) => stableSerialize(entry) ?? "null").join(",")}]`;
	if (value && typeof value === "object") {
		const entries = Object.keys(value)
			.sort()
			.filter((key) => value[key] !== undefined)
			.map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`);
		return `{${entries.join(",")}}`;
	}
	return JSON.stringify(value);
};

/**
 * React Query cache key for a cart quote, derived from the SAME normalized body
 * the request uses (key ≡ request by construction), serialized canonically so
 * producer object-key order cannot fragment the cache. The serialized body
 * carries item order and every response-affecting field, so distinct requests
 * key distinctly and identical requests share a cache entry.
 * @param {Array<Object>} items - ordered cart items
 * @param {string} [paymentType] - selected payment method, if any
 * @returns {Array} query key array
 */
export const cartQuoteKey = (items, paymentType) => [
	"cart-quote",
	stableSerialize(normalizeCartQuoteRequest(items, paymentType)),
];

/**
 * React Query hook for the display cart quote (POST /permits/cart).
 *
 * Options are set EXPLICITLY because the app-wide QueryClient (App.js:20) has no
 * defaultOptions and therefore carries the react-query library defaults
 * (retry 3, refetchOnWindowFocus true). This hook overrides them to match the
 * one-shot drawer/checkout behavior it replaces: no retry, no focus/reconnect
 * refetch, always-stale (staleTime 0), previous data kept while refetching for
 * a stable display.
 *
 * This hook is DISPLAY-ONLY. Enforcement (eligibility) checks must never read
 * through this cache — they issue a direct `getCartInformation` call.
 *
 * @param {Array<Object>} items - ordered cart items (redux cart snapshot)
 * @param {string} [paymentType] - selected payment method, if any
 * @param {Object} [queryOptions] - React Query overrides (e.g. enabled, onSuccess)
 * @returns {Object} React Query result { data, isLoading, isFetching, isError, error, refetch }
 */
export const useCartQuote = (items, paymentType, queryOptions = {}) => {
	const { items: normalizedItems } = normalizeCartQuoteRequest(items, paymentType);
	return useQuery({
		queryKey: cartQuoteKey(items, paymentType),
		queryFn: async () => {
			const response = await getCartInformation(normalizedItems, paymentType);
			return response?.data ?? response;
		},
		staleTime: 0,
		keepPreviousData: true,
		retry: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		...queryOptions,
	});
};

/**
 * Seeds the query cache with a quote result under the exact `cartQuoteKey`, so a
 * direct `getCartInformation` call's result can prime the display cache without
 * an extra fetch.
 * @param {import("@tanstack/react-query").QueryClient} queryClient - the client to seed
 * @param {Array<Object>} items - ordered cart items
 * @param {string} [paymentType] - selected payment method, if any
 * @param {Object} data - the quote result to cache
 * @returns {Object} the data set into the cache
 */
export const primeCartQuote = (queryClient, items, paymentType, data) =>
	queryClient.setQueryData(cartQuoteKey(items, paymentType), data);

export default useCartQuote;
